import { useParams, Link } from 'react-router-dom';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import PageMeta from '@/components/base/PageMeta';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  const categoryNameMap: Record<string, string> = {
    'quick-start': '快速入门',
    'layout-design': '布局设计',
    'custom-theme': '主题定制',
    'responsive': '响应式适配',
    'advanced': '高级技巧',
  };

  if (!article) {
    return (
      <>
        <PageMeta title="文章未找到" description="抱歉，您访问的文章不存在。" />
        <ScrollToTop />
        <Navbar />
        <div className="min-h-[60vh] flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-950 mb-3">文章未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您访问的文章不存在或已被移除。</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-200">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relatedArticles = getRelatedArticles(article, 3);
  const catName = categoryNameMap[article.category] || article.category;
  const schemaJson = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://bluenav.cn' },
          { '@type': 'ListItem', position: 2, name: catName, item: `https://bluenav.cn/category/${article.category}` },
          { '@type': 'ListItem', position: 3, name: article.title },
        ],
      },
      {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        author: { '@type': 'Person', name: article.author },
        datePublished: article.date,
        url: `https://bluenav.cn/article/${article.slug}`,
        inLanguage: 'zh-CN',
      },
    ],
  };

  return (
    <>
      <PageMeta
        title={article.title}
        description={article.excerpt}
        keywords={`蓝色小导航,${article.tags.join(',')},${catName}`}
        canonicalUrl={`https://bluenav.cn/article/${article.slug}`}
        ogType="article"
        schemaJson={schemaJson}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: catName, href: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <article className="py-12 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.category}`}
                  className="text-xs font-medium text-primary-600 bg-primary-50 px-3 py-1 rounded-full hover:bg-primary-100 transition-colors duration-200"
                >
                  {catName}
                </Link>
                <span className="text-xs text-foreground-400">{article.date}</span>
                <span className="text-xs text-foreground-400">{article.readTime}</span>
                <span className="text-xs text-foreground-400">{article.author}</span>
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
                {article.excerpt}
              </p>
            </div>

            <div className="mb-10 rounded-2xl overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-[320px] md:h-[420px] object-cover object-top"
              />
            </div>

            <div
              className="prose max-w-none text-foreground-800 leading-relaxed
                prose-headings:text-foreground-950 prose-headings:font-semibold
                prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                prose-p:text-sm md:prose-p:text-base prose-p:leading-relaxed prose-p:mb-5
                prose-ul:text-sm md:prose-ul:text-base prose-li:mb-2
                prose-strong:text-foreground-900
                prose-pre:bg-background-950 prose-pre:text-background-100 prose-pre:rounded-xl prose-pre:p-5 prose-pre:text-sm prose-pre:overflow-x-auto
                prose-code:bg-background-100 prose-code:text-primary-700 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded-md prose-code:text-xs prose-code:before:content-none prose-code:after:content-none
              "
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs text-secondary-600 bg-secondary-50 px-3 py-1 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-12 md:py-16 bg-background-100/60">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8">相关文章</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="group p-4 rounded-xl bg-background-50 border border-background-200/60 hover:border-primary-200/60 transition-all duration-200"
                  >
                    <div className="relative h-36 rounded-lg overflow-hidden mb-3">
                      <img
                        src={ra.imageUrl}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                      />
                    </div>
                    <h3 className="text-sm font-medium text-foreground-900 mb-1 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                      {ra.title}
                    </h3>
                    <p className="text-xs text-foreground-500">{ra.date} · {ra.readTime}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}